import type { ClipboardEvent } from 'react';
import { getFilesFromClipboardData } from 'v5/platform/dom/getFilesFromClipboardData';
import {
  canReadClipboard,
  readClipboardImage
} from 'v5/platform/dom/readClipboardImage';

export type ClipboardPasteResult = 'pasted' | 'empty' | 'failed';

/**
 * Files pasted into a field: `onPaste` takes the shortcut while focus is
 * inside it (so several fields on a screen never compete for one paste);
 * `pasteFromClipboard` reads the system clipboard for a Paste button.
 */
export function useClipboardFiles({
  accepts,
  onFiles
}: {
  accepts(file: File): boolean;
  onFiles(files: File[]): void;
}) {
  const onPaste = (event: ClipboardEvent<HTMLElement>) => {
    const files = getFilesFromClipboardData(event.clipboardData).filter(
      accepts
    );
    if (files.length === 0) return;
    event.preventDefault();
    onFiles(files);
  };

  const pasteFromClipboard = async (): Promise<ClipboardPasteResult> => {
    let image: File | null;
    try {
      image = await readClipboardImage();
    } catch {
      return 'failed';
    }
    if (!image) return 'empty';
    onFiles([image]);
    return 'pasted';
  };

  return { onPaste, pasteFromClipboard, canPaste: canReadClipboard() };
}
